import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import { Loader2 } from 'lucide-react';
import { ensureUsdtSymbol } from '@/utils/symbols';
import { useTradableTokens } from '@/hooks/useTradableTokens';
import { useUserExchanges } from '@/hooks/useExchanges';
import {
  searchTradableTokens,
  toFuturesServiceExchange,
  type FuturesServiceExchange,
  type TradableToken,
} from '@/lib/prices';

/*
 * Searchable list of tradable tokens (a perpetual on one of our exchanges)
 * for the "Enter Ticker Symbol" drawers, with a badge per exchange listing
 * the token and the user's connected exchanges highlighted.
 */

const EXCHANGE_BADGES: Record<FuturesServiceExchange, { name: string; logo: string }> = {
  binance_futures: { name: 'Binance', logo: '/exchanges-logo/binance.webp' },
  bybit_futures: { name: 'Bybit', logo: '/exchanges-logo/bybit.webp' },
  gate_futures: { name: 'Gate', logo: '/exchanges-logo/gateio.webp' },
  kucoin_futures: { name: 'KuCoin', logo: '/exchanges-logo/kucoin.webp' },
  hyperliquid: { name: 'Hyperliquid', logo: '/exchanges-logo/hyperliquid.webp' },
  aster_futures: { name: 'Aster', logo: '/exchanges-logo/aster.webp' },
};

/** Rows rendered at first and added each time the list is scrolled near its end. */
const PAGE_SIZE = 100;

/**
 * Public coin icon CDNs, tried in order. The phone loads them directly
 * (never through our backend) and sends no referrer.
 */
const COIN_LOGO_SOURCES = [
  (base: string) => `https://icon.gateimg.com/images/coin_icon/64/${base.toLowerCase()}.png`,
  (base: string) => `https://bin.bnbstatic.com/static/assets/logos/${base}.png`,
];

/** `1000PEPE` -> `PEPE`: multiplier contracts use the coin's own logo. */
function logoBase(base: string): string {
  return base.replace(/^10{2,}(?=[A-Z])/, '');
}

const CoinLogo: React.FC<{ base: string }> = ({ base }) => {
  const [source, setSource] = useState(0);
  const coin = logoBase(base);
  if (source >= COIN_LOGO_SOURCES.length) {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-semibold text-text-secondary">
        {coin.slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      src={COIN_LOGO_SOURCES[source](coin)}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setSource((index) => index + 1)}
      className="h-8 w-8 shrink-0 rounded-full bg-white/5"
    />
  );
};

function formatVolume(volume: number | null): string | null {
  if (volume === null || volume <= 0) return null;
  if (volume >= 1e9) return `$${(volume / 1e9).toFixed(1)}B`;
  if (volume >= 1e6) return `$${(volume / 1e6).toFixed(1)}M`;
  if (volume >= 1e3) return `$${(volume / 1e3).toFixed(0)}K`;
  return `$${volume.toFixed(0)}`;
}

const TokenRow: React.FC<{
  token: TradableToken;
  connected: Set<FuturesServiceExchange>;
  selected: boolean;
  onSelect: (token: TradableToken) => void;
}> = ({ token, connected, selected, onSelect }) => {
  // The user's exchanges first, so "can I trade it?" reads at a glance.
  const exchanges = useMemo(
    () =>
      [...token.exchanges].sort(
        (a, b) => Number(connected.has(b)) - Number(connected.has(a))
      ),
    [token.exchanges, connected]
  );
  const onMine = connected.size === 0 || token.exchanges.some((ex) => connected.has(ex));
  const volume = formatVolume(token.volume24h);

  return (
    <button
      type="button"
      data-testid={`ticker-option-${token.base}`}
      onClick={() => onSelect(token)}
      className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${
        selected ? 'bg-accent/20' : 'hover:bg-white/5'
      }`}
    >
      <CoinLogo key={token.base} base={token.base} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-text-primary">{token.base}</div>
        <div className="truncate text-[11px] text-text-secondary">
          {onMine ? (volume ? `24h vol ${volume}` : 'Perpetual') : 'Not on your exchanges'}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {exchanges.map((exchange) => {
          const badge = EXCHANGE_BADGES[exchange];
          const mine = connected.has(exchange);
          return (
            <img
              key={exchange}
              src={badge.logo}
              alt={badge.name}
              title={mine ? `${badge.name} (connected)` : badge.name}
              loading="lazy"
              className={`h-5 w-5 rounded-full ${
                connected.size === 0 || mine
                  ? 'ring-1 ring-accent'
                  : 'opacity-35 grayscale'
              }`}
            />
          );
        })}
      </div>
    </button>
  );
};

/**
 * State for a ticker picker: the search, the picked token (or the listed
 * token the search names exactly) and the list rendering.
 */
export function useTickerPicker(open: boolean) {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || '';
  const [query, setQueryState] = useState('');
  const [picked, setPicked] = useState<TradableToken | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const listRef = useRef<HTMLDivElement>(null);

  const tokensQuery = useTradableTokens(open);
  const { data: userExchanges } = useUserExchanges(userId || undefined);

  const connected = useMemo(() => {
    const set = new Set<FuturesServiceExchange>();
    for (const exchange of userExchanges ?? []) {
      const service = toFuturesServiceExchange(exchange.code);
      if (service) set.add(service);
    }
    return set;
  }, [userExchanges]);

  const matches = useMemo(
    () => searchTradableTokens(tokensQuery.data ?? [], query),
    [tokensQuery.data, query]
  );

  // A typed ticker that is listed counts as picked, so Enter just works.
  const typedBase = ensureUsdtSymbol(query.trim() || 'X').slice(0, -4);
  const exactMatch = matches[0]?.base === typedBase ? matches[0] : null;
  const selected = picked ?? exactMatch;

  const setQuery = useCallback((value: string) => {
    setQueryState(value.toUpperCase());
    setPicked(null);
    setVisibleCount(PAGE_SIZE);
    listRef.current?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (open) setQuery('');
  }, [open, setQuery]);

  const onListScroll = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 400) {
      setVisibleCount((count) => (count < matches.length ? count + PAGE_SIZE : count));
    }
  }, [matches.length]);

  return {
    query,
    setQuery,
    selected,
    pick: setPicked,
    matches,
    visibleCount,
    connected,
    tokensQuery,
    listRef,
    onListScroll,
  };
}

export type TickerPickerState = ReturnType<typeof useTickerPicker>;

export const ConnectedExchangesHint: React.FC<{ picker: TickerPickerState }> = ({ picker }) =>
  picker.connected.size > 0 ? (
    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-text-secondary">
      <span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-accent" />
      Highlighted logos are your connected exchanges
    </p>
  ) : null;

export const TickerOptions: React.FC<{ picker: TickerPickerState; className?: string }> = ({
  picker,
  className = '',
}) => {
  const { tokensQuery, matches, query, visibleCount, connected, selected } = picker;
  return (
    <div
      ref={picker.listRef}
      onScroll={picker.onListScroll}
      className={`overflow-y-auto ${className}`}
      data-testid="ticker-options"
    >
      {tokensQuery.isLoading && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tokens
        </div>
      )}
      {tokensQuery.isError && (
        <p className="px-2 py-6 text-center text-sm text-text-secondary">
          Couldn't load the token list. You can still type a ticker.
        </p>
      )}
      {tokensQuery.isSuccess && matches.length === 0 && (
        <p className="px-2 py-6 text-center text-sm text-text-secondary">
          {query.trim()
            ? 'No perpetual on our exchanges matches that ticker'
            : 'No tokens available right now'}
        </p>
      )}
      {matches.slice(0, visibleCount).map((token) => (
        <TokenRow
          key={token.symbol}
          token={token}
          connected={connected}
          selected={selected?.symbol === token.symbol}
          onSelect={picker.pick}
        />
      ))}
    </div>
  );
};
